import * as React from 'react';

import { observer } from 'mobx-react';

import { Form } from '@estee/elc-form';

import { FormContext } from '~context/FormContext';
import { IFieldsValidations, IFormSchema } from '~domain/entities/Config';
import { RampFormStore } from '~domain/entities/RampFormStore';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

import { DynamicFormViewController } from './DynamicFormViewController';
import { SectionGrid } from './SectionGrid';
import { SectionHeader } from './SectionHeader';

interface IDynamicFormProps {
    name: string;
    className?: string;
    formSchema: IFormSchema;
    validations: IFieldsValidations;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formStore: RampFormStore<any>;
    submit(): Promise<void>;
}

@observer
export class DynamicForm extends React.Component<IDynamicFormProps> {
    private dynamicFormViewController: DynamicFormViewController;
    private formWrapperRef: React.RefObject<HTMLDivElement>;

    constructor(props: IDynamicFormProps) {
        super(props);
        this.dynamicFormViewController = diContainer.get(serviceNames.dynamicFormViewController);
        this.formWrapperRef = React.createRef();
    }

    async componentDidMount() {
        const { formSchema, validations } = this.props;
        await this.dynamicFormViewController.loaded(formSchema, validations);
        this.formWrapperRef.current?.addEventListener('keypress', this.handleKeyPress);
    }

    componentDidUpdate() {
        this.formWrapperRef.current?.addEventListener('keypress', this.handleKeyPress);
    }

    componentWillUnmount() {
        this.formWrapperRef.current?.removeEventListener('keypress', this.handleKeyPress);
    }

    // Can't bind directly to div element due to a11y error: Static HTML elements with event handlers require a role  jsx-a11y/no-static-element-interactions
    handleKeyPress = (event: KeyboardEvent) => {
        const {
            formStore: { isSubmitting },
            submit
        } = this.props;

        if (event.key === 'Enter' && !isSubmitting) {
            submit();
        }
    };

    private renderForm = () => {
        const { formStore, formSchema } = this.props;
        const {
            data: { translations }
        } = this.dynamicFormViewController;

        return (
            <Form
                store={formStore}
                render={() =>
                    formSchema.sections.map((section) => {
                        const { titleLabelKey, name: sectionName, items } = section;

                        return (
                            <React.Fragment key={sectionName}>
                                {titleLabelKey && (
                                    <SectionHeader
                                        name={sectionName}
                                        // TODO: APACRIGEL-1130 consume translation inside
                                        title={translations[titleLabelKey]}
                                    />
                                )}
                                <SectionGrid
                                    formStore={formStore}
                                    items={items}
                                    sectionName={sectionName}
                                />
                            </React.Fragment>
                        );
                    })
                }
            />
        );
    };

    render() {
        const { name: formName } = this.props;
        const {
            data: {
                initialized,
                fieldsValidations,
                readonlyFields,
                highlightedFields,
                removeHighlight
            }
        } = this.dynamicFormViewController;

        return (
            initialized && (
                <div ref={this.formWrapperRef}>
                    <FormContext.Provider
                        value={{
                            fieldsValidations,
                            readonlyFields,
                            highlightedFields,
                            formName,
                            removeHighlight
                        }}
                    >
                        {this.renderForm()}
                    </FormContext.Provider>
                </div>
            )
        );
    }
}
